import { useTranslation } from 'react-i18next'
import { AccessCard } from '../components/settings/AccessCard'
import { PageHeader } from '../components/ui/PageHeader'
import { PageSaveDiscard } from '../components/ui/PageSaveDiscard'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { Tabs } from '../components/ui/Tabs'
import { usePortalAccess } from '../hooks/usePortalAccess'
import { usePortalRoleDrafts } from '../hooks/usePortalRoleDrafts'
import { PAGES } from '../lib/routes'

const ACCESS_TAB = PAGES.settings.fixedTab?.slug ?? 'access'
const TABS = [ACCESS_TAB] as const

function stayOnTab() {
  // One fixed tab (prototype `FIXED_TABS`): nothing to switch to.
}

/** Settings (MS-997 D4; spec §1-ctrl-tab) — the prototype's
 * `#section-settings`, Admin only (app/RequirePortalAdmin.tsx guards the
 * route): the page header "Settings / Manage the Subscription Portal
 * application", one fixed "Access" tab, and the access card. Never a card
 * on Home.
 *
 * Not in the prototype (ARCHITECTURE_DECISIONS.md §64, at the user's
 * request): the header's Save / Discard pair, the one Excess Volume and User
 * Limits use. Permission changes are staged (hooks/usePortalRoleDrafts.ts)
 * and saved together; Add user and Remove still apply through their own
 * confirmation. */
export function Settings() {
  const { t } = useTranslation()
  const access = usePortalAccess()
  const roles = usePortalRoleDrafts(access)
  return (
    <PageView>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.caption')}
        actions={
          <PageSaveDiscard
            dirty={roles.dirty}
            busy={roles.saving}
            onSave={() => void roles.save()}
            onDiscard={roles.discard}
          />
        }
      />
      <Tabs value={ACCESS_TAB} onChange={stayOnTab} options={TABS} labelFor={() => t('settings.tabs.access')} />
      <PageScrollArea>
        <AccessCard access={access} roles={roles} />
      </PageScrollArea>
    </PageView>
  )
}
